import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { useAuth } from "../../hooks/useAuth";
import { clientService } from "../../services/clientService";
import { mealPlanService } from "../../services/mealPlanService";
import { workoutPlanService } from "../../services/workoutPlanService";
import type { MealPlan, WorkoutPlan } from "../../types/coaching";
import type { ClientProfile } from "../../types/trainer";

export function ClientPage() {
  const { user } = useAuth();
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadClient() {
      if (!user) return;
      setLoading(true);
      setError(null);

      try {
        const currentClient = await clientService.getCurrentClient(user.id);
        const [assignedWorkouts, assignedMeals] = currentClient
          ? await Promise.all([
            workoutPlanService.listAssignedWorkoutPlansForClient(currentClient.id),
            mealPlanService.listAssignedMealPlansForClient(currentClient.id),
          ])
          : [[], []];
        if (mounted) {
          setClient(currentClient);
          setWorkoutPlans(assignedWorkouts);
          setMealPlans(assignedMeals);
        }
      } catch (caughtError) {
        console.error("Unable to load client portal", caughtError);
        if (mounted) setError("Unable to load your client profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void loadClient();

    return () => {
      mounted = false;
    };
  }, [user]);

  if (loading) return <FullPageLoader label="Loading client portal" />;
  if (error) return <EmptyState title="Unable to load client portal.">{error}</EmptyState>;
  if (!client) return <EmptyState title="No client profile found." />;

  const currentWorkout = workoutPlans[0];
  const currentMeal = mealPlans[0];

  if (client.status === "onboarding") {
    return (
      <EmptyState eyebrow="Client portal" title="Finish your onboarding">
        Your account is active, but your coach needs your onboarding details before the next step.
        <div className="mt-5">
          <Link to="/client/onboarding"><Button>Continue onboarding</Button></Link>
        </div>
      </EmptyState>
    );
  }

  return (
    <div>
      <p className="eyebrow">Client portal</p>
      <h1 className="mt-3 text-3xl font-semibold text-ink">Welcome, {client.full_name}.</h1>
      <p className="mt-3 max-w-3xl text-base leading-7 text-graphite/70">
        Your onboarding is complete. Your coach can now review your details and assign plans.
      </p>
      <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <AdminPanel
          title="Current workout plan"
          description={currentWorkout ? currentWorkout.title : "Your coach has not assigned a workout plan yet."}
        >
          <Link to="/client/workouts"><Button>Open workouts</Button></Link>
        </AdminPanel>
        <AdminPanel
          title="Current meal plan"
          description={currentMeal ? currentMeal.title : "Your coach has not assigned a meal plan yet."}
        >
          <Link to="/client/meals"><Button>Open meals</Button></Link>
        </AdminPanel>
        <AdminPanel title="Glossary" description="Look up exercises, nutrition terms and coaching language.">
          <Link to="/client/glossary"><Button>Open glossary</Button></Link>
        </AdminPanel>
        <AdminPanel title="Onboarding" description={`Current profile status: ${client.status}.`}>
          <Link to="/client/onboarding"><Button variant="secondary">Review onboarding</Button></Link>
        </AdminPanel>
      </div>
    </div>
  );
}
